import React, { useCallback, useEffect, useState } from "react";
import cls from "classnames";
import { FlatList, Toast } from "@al/akui-mobile";
import { useCtx } from "@al/next/al-ex";

import { CouponItemModel, CouponStatus } from "~/common/model/coupon";

import ErrorBlock from "../../ErrorBlock";
import { CouponItem } from "../coupon-item";
import { NoCoupon } from "../no-coupon";
import ListFooter from "../../list-footer";
import Loading from "../../Loading";

import styles from "./styles.scss";

type PostData = {
  pageNo: number;
  pageSize: number;
};

type ListInfo = { records: CouponItemModel[]; isLast: boolean };

export interface CouponListProps {
  className?: string;
  footer?: React.ReactNode;
  wrapperRef?: React.RefObject<HTMLDivElement>;
  /**
   * 接口链接
   */
  interfaceUri: string;
  couponStatus?: CouponStatus;
  /**
   * 优惠券去使用的回调函数
   */
  onClickOperateBtn?: () => void;
}

const WrappedCouponList = (props: CouponListProps): JSX.Element => {
  const { className, footer, wrapperRef, interfaceUri, couponStatus, onClickOperateBtn } = props;
  const ctx = useCtx();
  const [listInfo, setListInfo] = useState<ListInfo>({
    records: [],
    isLast: false,
  });

  const [loading, seLoading] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);

  const [postData, setPostData] = useState<PostData>({
    pageNo: 1,
    pageSize: 10,
  });

  const getList = useCallback(
    async (param: PostData = postData): Promise<void> => {
      if (!interfaceUri) {
        return;
      }
      param !== postData && setPostData(param);
      seLoading(true);
      const { success, data, errMsg } = await ctx.io.post<{ records: CouponItemModel[]; isLast: boolean }>(
        interfaceUri,
        {
          ...param,
          countryId: ctx.locale.countryId,
          couponStatus,
        }
      );
      // 测试代码
      if (success) {
        const records = data?.records || [];
        setListInfo({
          records: param.pageNo === 1 ? records : [...listInfo.records, ...records],
          isLast: Boolean(data.isLast),
        });
        setPostData({
          ...param,
          pageNo: param.pageNo + 1,
        });
      } else {
        // 如果第一页请求数据就报错，直接显示网络错误
        if (param.pageNo === 1) {
          setIsNetworkError(true);
        }
        Toast.show({ content: errMsg });
      }
      seLoading(false);
    },
    [postData, loading, ctx, isNetworkError, listInfo]
  );

  useEffect(() => {
    // 获取商品列表
    getList({
      pageNo: 1,
      pageSize: 10,
    });
  }, [couponStatus]);

  const handleLoadMore = useCallback(() => {
    if (!listInfo.isLast) {
      getList();
    }
  }, [listInfo, getList]);

  // onRefresh必须是个异步函数
  const handleRefresh = useCallback(async () => {
    // 重置页数
    getList({
      ...postData,
      pageNo: 1,
    });
  }, [postData, getList]);

  const renderItem = useCallback(
    (data: { item: CouponItemModel; index: number }) => {
      return (
        <CouponItem
          operateType="use"
          couponStatus={couponStatus}
          data={data.item}
          key={`${data.item.couponId}_${data.index}`}
          onClickOperateBtn={onClickOperateBtn}
        />
      );
    },
    [couponStatus]
  );

  return (
    <div
      className={cls(
        styles.coupon_list,
        {
          [styles.no_coupon]: !listInfo.records.length || isNetworkError,
        },
        className
      )}
      ref={wrapperRef}
    >
      {isNetworkError ? (
        <ErrorBlock />
      ) : (
        <FlatList
          onRefresh={handleRefresh}
          onEndReached={handleLoadMore}
          data={listInfo.records}
          onEndReachedThreshold={0.1}
          endReachedTime={1000}
          renderItem={renderItem}
          ListEmptyComponent={<NoCoupon className={!loading && listInfo.isLast ? undefined : styles.hidden} />}
          ItemSeparatorComponent={<div className={styles.separator}></div>}
        />
      )}
      {/* 初次loading显示 */}
      {loading && postData.pageNo === 1 && <Loading visible />}
      {footer ? footer : <ListFooter isLast={listInfo.isLast} loading={loading} />}
    </div>
  );
};

export const CouponList = React.memo(WrappedCouponList);
